// Sends the events trackEvent already emits to our own backend, so the console can answer
// "what did this person actually do?" — a question Google Analytics cannot answer, because it
// reports on traffic rather than on the individual who signed up last Tuesday.

import { authHeader } from './authToken';

const API_URL = (process.env.REACT_APP_AI_API_URL || 'http://localhost:8080').replace(/\/$/, '');
const SESSION_KEY = 'ma_activity_session_v1';

/** Sent together rather than one request per click. */
const MAX_BATCH = 50;
const FLUSH_AFTER_MS = 8000;

let queue = [];
let timer = null;

/**
 * A visit identifier, so events from a signed-out browser still form a story.
 *
 * <p>Kept in sessionStorage, not localStorage: it should die with the tab rather than follow
 * someone across weeks. It is random and meaningless on its own — nothing about the person is
 * derived from or encoded in it.
 */
function sessionId() {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private browsing can refuse storage. A per-load id still groups one page's events.
    return `ephemeral-${Date.now()}`;
  }
}

/** Whatever the caller passed to trackEvent, reduced to something short and readable. */
function summarise(params) {
  if (!params || typeof params !== 'object') return undefined;
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'object') continue;
    parts.push(`${key}=${String(value)}`);
    if (parts.join(' ').length > 400) break;
  }
  const detail = parts.join(' ');
  return detail ? detail.slice(0, 500) : undefined;
}

function flush({ useBeacon = false } = {}) {
  if (queue.length === 0) return;

  const batch = { sessionId: sessionId(), events: queue.slice(0, MAX_BATCH) };
  queue = queue.slice(MAX_BATCH);
  clearTimeout(timer);
  timer = null;

  const url = `${API_URL}/api/activity`;
  const payload = JSON.stringify(batch);

  // On page-hide a normal fetch is cancelled as the document goes away; sendBeacon is
  // handed to the browser to deliver afterwards. It cannot carry an Authorization header,
  // so those events arrive unattributed — acceptable, since the alternative is losing them.
  if (useBeacon && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    } catch {
      /* fall through to fetch */
    }
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Analytics must never break the page it is measuring.
  });
}

/** Queued by trackEvent. Consent is checked there, not here. */
export function recordActivity(name, params) {
  try {
    queue.push({
      event: String(name).slice(0, 64),
      path: window.location?.pathname?.slice(0, 500),
      detail: summarise(params),
      at: new Date().toISOString(),
    });

    if (queue.length >= MAX_BATCH) {
      flush();
    } else if (!timer) {
      timer = setTimeout(() => flush(), FLUSH_AFTER_MS);
    }
  } catch {
    /* never let measurement throw into a render */
  }
}

/** Call once on app load. */
export function startActivityLog() {
  if (typeof window === 'undefined') return;

  // visibilitychange rather than unload: unload does not fire reliably on mobile Safari, which
  // is where a good share of this traffic is.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush({ useBeacon: true });
  });
  window.addEventListener('pagehide', () => flush({ useBeacon: true }));
}
